import { apiRequest } from '@/lib/httpClient'
import type {
  AllocationCandidatesResult,
  AllocationRecommendation,
  CapacityDashboardData,
  CapacityJobBudget,
  CapacitySettings,
  JobAllocation,
  PagedResult,
  RemoveSourceAllocationStaffPayload,
  ReplaceJobAllocationsPayload,
  SaveAllocationPayload,
  SaveAllocationsOptions,
  SaveJobBudgetPayload,
  SaveSettingsPayload,
  SourceAllocationStaff,
  StaffAvailability,
  ValidateSourceAllocationStaffRemovalPayload,
  WorkflowJobsResult,
} from './types'

const EMPTY_DATE_VALUES = new Set(['0001-01-01', '0001-01-01T00:00:00', '0001-01-01T00:00:00Z'])

export async function fetchCapacitySettings(clientId: string): Promise<CapacitySettings | null> {
  const result = await apiRequest<CapacitySettings | null>('CapacityPlanner/settings', {
    method: 'GET',
    query: { clientId },
  })
  return result ?? null
}

export async function saveCapacitySettings(
  clientId: string,
  payload: SaveSettingsPayload
): Promise<CapacitySettings> {
  return apiRequest<CapacitySettings>('CapacityPlanner/settings/save', {
    method: 'POST',
    body: { clientId, ...payload },
  })
}

export async function fetchCapacityData(
  clientId: string,
  startDate: string,
  endDate: string,
  options?: { team?: string; search?: string; weekLabel?: string; periodStart?: string; page?: number; pageSize?: number }
): Promise<CapacityDashboardData> {
  return apiRequest<CapacityDashboardData>('CapacityPlanner/capacity', {
    method: 'GET',
    query: {
      clientId,
      startDate,
      endDate,
      team: options?.team,
      search: options?.search,
      weekLabel: options?.weekLabel,
      periodStart: options?.periodStart,
      page: options?.page,
      pageSize: options?.pageSize,
    },
  })
}

export async function fetchWorkflowJobs(
  clientId: string,
  options?: { staffId?: string; state?: string; search?: string; page?: number; pageSize?: number }
): Promise<WorkflowJobsResult> {
  const result = await apiRequest<WorkflowJobsResult>('CapacityPlanner/workflow', {
    method: 'GET',
    query: {
      clientId,
      staffId: options?.staffId,
      state: options?.state,
      search: options?.search,
      page: options?.page,
      pageSize: options?.pageSize,
    },
  })

  return {
    ...result,
    stateOptions: result.stateOptions ?? [],
    staffOptions: result.staffOptions ?? [],
    items: result.items.map((job) => ({
      ...job,
      dueDate: normalizeOptionalDate(job.dueDate),
    })),
  }
}

export async function fetchAllocationCandidates(
  clientId: string,
  options?: { allocationStatus?: string; state?: string; search?: string; page?: number; pageSize?: number }
): Promise<AllocationCandidatesResult> {
  const result = await apiRequest<AllocationCandidatesResult>('CapacityPlanner/allocation-candidates', {
    method: 'GET',
    query: {
      clientId,
      allocationStatus: options?.allocationStatus,
      state: options?.state,
      search: options?.search,
      page: options?.page,
      pageSize: options?.pageSize,
    },
  })

  return {
    ...result,
    stateOptions: result.stateOptions ?? [],
  }
}

export async function fetchSourceAllocationStaff(
  clientId: string,
  jobId: string
): Promise<SourceAllocationStaff[]> {
  return apiRequest<SourceAllocationStaff[]>('CapacityPlanner/source-allocation-staff', {
    method: 'GET',
    query: {
      clientId,
      jobId,
    },
  })
}

export async function fetchAllocations(
  clientId: string,
  weekStart: string,
  weekEnd: string,
  options?: { search?: string; staffId?: string; page?: number; pageSize?: number }
): Promise<PagedResult<JobAllocation>> {
  return apiRequest<PagedResult<JobAllocation>>('CapacityPlanner/allocations', {
    method: 'GET',
    query: {
      clientId,
      weekStart,
      weekEnd,
      search: options?.search,
      staffId: options?.staffId,
      page: options?.page,
      pageSize: options?.pageSize,
    },
  })
}

export async function fetchStaffAvailability(
  clientId: string,
  weekStart: string,
  weekEnd: string,
  options?: { search?: string; staffId?: string; page?: number; pageSize?: number }
): Promise<PagedResult<StaffAvailability>> {
  return apiRequest<PagedResult<StaffAvailability>>('CapacityPlanner/staff-availability', {
    method: 'GET',
    query: {
      clientId,
      weekStart,
      weekEnd,
      search: options?.search,
      staffId: options?.staffId,
      page: options?.page,
      pageSize: options?.pageSize,
    },
  })
}

export async function saveAllocations(
  clientId: string,
  allocations: SaveAllocationPayload[],
  options: SaveAllocationsOptions = {}
): Promise<JobAllocation[]> {
  return apiRequest<JobAllocation[]>('CapacityPlanner/allocations/save', {
    method: 'POST',
    body: { clientId, allocations, ...options },
  })
}

export async function replaceJobAllocations(
  clientId: string,
  payload: ReplaceJobAllocationsPayload
): Promise<JobAllocation[]> {
  return saveAllocations(clientId, payload.allocations, {
    replaceJobAllocations: true,
    jobId: payload.jobId,
    weekStart: payload.weekStart,
    weekEnd: payload.weekEnd,
    jobNote: payload.jobNote,
  })
}

export async function deleteAllocation(allocationId: string): Promise<void> {
  await apiRequest<void>('CapacityPlanner/allocations/delete', {
    method: 'POST',
    body: { allocationId },
  })
}

export async function removeSourceAllocationStaff(
  clientId: string,
  payload: RemoveSourceAllocationStaffPayload
): Promise<void> {
  await apiRequest<void>('CapacityPlanner/allocations/source-staff/remove', {
    method: 'POST',
    body: { clientId, ...payload },
  })
}

export async function validateSourceAllocationStaffRemoval(
  clientId: string,
  payload: ValidateSourceAllocationStaffRemovalPayload
): Promise<void> {
  await apiRequest<void>('CapacityPlanner/allocations/source-staff/validate-removal', {
    method: 'POST',
    body: { clientId, ...payload },
  })
}

export async function fetchAllocationRecommendations(
  clientId: string,
  jobIds: string[],
  weekStart: string,
  weekCount: number
): Promise<AllocationRecommendation> {
  return apiRequest<AllocationRecommendation>('CapacityPlanner/allocation-recommendations', {
    method: 'POST',
    body: { clientId, jobIds, weekStart, weekCount },
  })
}

export async function saveJobBudget(
  clientId: string,
  budget: SaveJobBudgetPayload
): Promise<CapacityJobBudget> {
  return apiRequest<CapacityJobBudget>('CapacityPlanner/job-budgets/save', {
    method: 'POST',
    body: { clientId, budget },
  })
}

export async function saveJobBudgets(
  clientId: string,
  budgets: SaveJobBudgetPayload[]
): Promise<CapacityJobBudget[]> {
  return apiRequest<CapacityJobBudget[]>('CapacityPlanner/job-budgets/bulk-save', {
    method: 'POST',
    body: { clientId, budgets },
  })
}

function normalizeOptionalDate(value: string | null | undefined) {
  if (value == null) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  return EMPTY_DATE_VALUES.has(trimmed) ? null : trimmed
}
